// The tappable bubble grid shared by every screen that shows a paper's answers:
// post-grade review (scanController) and the persisted Result Detail. One
// implementation, so the outcome markers can't drift between the two screens.
import { esc } from './dom.js';
import { classifyOutcome, type Outcome } from '../scoring/score.js';
import type { AnswerKeyRecord } from '../db/schema.js';

/** The minimum a row needs: the engine's raw read for one item. */
export interface GridItem {
  q: number;
  chosen: string | null;
  flags: readonly string[];
}

/**
 * Outcome markers. Colour ALONE never carries the meaning — each outcome has its
 * own glyph too, so the grid still reads correctly for a colourblind teacher and
 * in a black-and-white screenshot. `double` is deliberately amber with its own
 * glyph rather than red: a double-mark is an ambiguity the engine flagged for a
 * human, not a plain miss, and showing it as "wrong" would hide that.
 */
export const OUTCOME_MARKERS: Record<Exclude<Outcome, 'unscored'>, { glyph: string; label: string }> = {
  correct: { glyph: '✓', label: 'correct' },
  wrong: { glyph: '✗', label: 'wrong' },
  blank: { glyph: '–', label: 'blank' },
  double: { glyph: '!', label: 'double mark' },
};

/**
 * One row: outcome marker + item number + the option bubbles.
 *
 * The marker sits inside the row but is not itself a control — only the bubbles
 * are tappable, so tapping to correct an item works exactly as before.
 *
 * The `.key` highlight stays keyed off the plain `key.answers` map on purpose: a
 * TESTIFY hashed key must never have its correct option rendered on screen. The
 * outcome marker is safe for hashed keys — it's a right/wrong verdict, not the key.
 */
export function answerRowHtml(a: GridItem, opts: string[], key: AnswerKeyRecord | null | undefined): string {
  const cls = a.flags.includes('double') || a.flags.includes('blank') ? 'flag' : a.flags.includes('review') ? 'review' : '';
  const keyOpt = key?.answers?.[a.q];
  const outcome = classifyOutcome(key, a.q, a);
  const m = outcome === 'unscored' ? null : OUTCOME_MARKERS[outcome];
  // unscored (no key yet) renders no marker at all — the grid looks untouched.
  const mark = m ? `<i class="mk mk-${outcome}" aria-hidden="true">${m.glyph}</i>` : '';
  const srLabel = m ? ` <span class="sr-only">(${m.label})</span>` : '';
  return `<div class="qrow ${cls}" data-row="${a.q}" data-outcome="${outcome}"><span class="qn">${mark}${a.q}${srLabel}</span><span class="opts">${opts
    .map((o) => `<button type="button" class="opt ${a.chosen === o ? 'on' : ''} ${keyOpt === o ? 'key' : ''}" data-q="${a.q}" data-opt="${esc(o)}" aria-label="Q${a.q} ${esc(o)}">${esc(o)}</button>`)
    .join('')}</span></div>`;
}

/** The whole grid's inner HTML (the caller owns the `.answers-grid` element). */
export function answerGridHtml(answers: readonly GridItem[], opts: string[], key: AnswerKeyRecord | null | undefined): string {
  return answers.map((a) => answerRowHtml(a, opts, key)).join('');
}
